import {
  ExtractionMessage,
  ExtractionProgress,
  ExtractPdf,
  MaterialPrepError,
  PdfTextResult,
} from './extractionTypes';

/**
 * The subset of `Worker` that `runExtractionWorker` needs, so a test can hand it a fake
 * worker instead of a real module worker (PTX-D9).
 */
export type WorkerLike = Pick<
  Worker,
  'postMessage' | 'terminate' | 'addEventListener' | 'removeEventListener'
>;

/**
 * Drives a PDF extraction worker end to end: reads `file` into an `ArrayBuffer`, posts it to a
 * freshly created worker, forwards its progress and settles on `done`/`failed` (PTX-D9).
 * Picking another file aborts `signal`, which terminates the worker (PTX-D9). The worker is
 * always terminated exactly once, however this settles.
 */
export const runExtractionWorker = (
  createWorker: () => WorkerLike,
  file: Blob,
  onProgress: (progress: ExtractionProgress) => void,
  signal: AbortSignal
): Promise<PdfTextResult> => {
  return new Promise<PdfTextResult>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    let worker: WorkerLike | undefined;
    let settled = false;

    const cleanup = (): void => {
      if (worker) {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
        worker.removeEventListener('messageerror', onMessageError);
        worker.terminate();
      }
      signal.removeEventListener('abort', onAbort);
    };

    const settle = (fn: () => void): void => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      fn();
    };

    const onMessage = (event: MessageEvent<ExtractionMessage>): void => {
      const message = event.data;
      if (message.type === 'progress') {
        onProgress({ page: message.page, pdfPageCount: message.pdfPageCount });
      } else if (message.type === 'done') {
        settle(() => resolve({ text: message.text, ocrPages: message.ocrPages }));
      } else if (message.type === 'failed') {
        settle(() => reject(new MaterialPrepError(message.code)));
      }
      // Any other message (e.g. pdf.js's worker module posting an unrequested
      // `{ sourceName: 'worker', targetName: 'main', action: 'ready', ... }` to the main
      // thread) is ignored.
    };

    const onError = (): void => {
      settle(() => reject(new MaterialPrepError('unknown')));
    };

    const onMessageError = (): void => {
      settle(() => reject(new MaterialPrepError('unknown')));
    };

    const onAbort = (): void => {
      settle(() => reject(new DOMException('Aborted', 'AbortError')));
    };

    signal.addEventListener('abort', onAbort);

    file
      .arrayBuffer()
      .then((data) => {
        if (settled) {
          return;
        }
        worker = createWorker();
        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        worker.addEventListener('messageerror', onMessageError);
        worker.postMessage({ type: 'extract', data }, [data]);
      })
      .catch(() => {
        settle(() => reject(new MaterialPrepError('unknown')));
      });
  });
};

/**
 * Reads `file`'s text in a dedicated module Web Worker, so the main thread stays responsive
 * while pdf.js parses the PDF (PTX-D9). The `new Worker(new URL(...), { type: 'module' })`
 * shape must be written out literally here: Vite only recognises that exact pattern when
 * bundling the worker (issue 13 adds `./extraction.worker.ts`).
 */
export const extractPdf: ExtractPdf = (file, onProgress, signal) =>
  runExtractionWorker(
    () => new Worker(new URL('./extraction.worker.ts', import.meta.url), { type: 'module' }),
    file,
    onProgress,
    signal
  );
